import React, { useState, useEffect } from "react";
import { useAuth } from "hooks/useAuth";
import { osirisApi } from "services/api/osiris";
import { Row, Col } from "react-bootstrap";
import { SelectBox } from "devextreme-react/select-box";
import { useGroupedItems } from "hooks/useGroupedItems";
import {
  getClientPalette,
  defaultPalette,
} from "../../../../../config/clientPalettes";
import BreakdownByRating from "./BreakdownByRating";
import BreakdownByIssuer from "./BreakdownByIssuer";
import BreakdownByBucket from "./BreakdownByBucket";
import BreakdownBySector from "./BreakdownBySector";
import BreakdownByMaturity from "./BreakdownByMaturity";
import BreakdownByCountry from "./BreakdownByCountry";
import BreakdownByCountryMap from "./BreakdownByCountryMap";
import PyramidCard from "components/common/pyramid/PyramidCard";
import EmptyCard from "components/common/pyramid/EmptyCard";

/**
 * ComputationCharts Component
 *
 * Main dashboard component for displaying various financial charts and breakdowns.
 * Fetches computation data, processes it, and distributes it to child components.
 */
const ComputationCharts = () => {
  // State management for chart data, computations, and UI controls
  const [chartData, setChartData] = useState({
    ratingData: [],
    issuerData: [],
    sectorData: [],
    maturityData: [],
    countryData: [],
    countryMapData: [],
    bucketData: [],
    totalEcl: 0,
    eclData: null,
  });
  const [computations, setComputations] = useState([]);
  const [selectedComputation, setSelectedComputation] = useState(null);
  const [currentPalette, setCurrentPalette] = useState(defaultPalette);
  const [isLoading, setIsLoading] = useState(true);

  // Get authentication data and API instance
  const { accessToken, account, companyList, userList } = useAuth();
  const api = osirisApi();

  // Grouping computations by user
  const groupedComputations = useGroupedItems(
    computations,
    userList,
    companyList
  );

  /**
   * Effect to update the color palette when the user account changes
   * Each client may have a custom color palette configuration
   */
  useEffect(() => {
    if (account && companyList && companyList.length > 0) {
      const newPalette = getClientPalette(companyList[0].id || "default");
      setCurrentPalette(newPalette);
    }
  }, [account, companyList]);

  /**
   * Effect to fetch available computations when component mounts
   * Retrieves only the completed/ready computations and sorts them by creation date
   */
  useEffect(() => {
    const fetchComputations = async () => {
      try {
        if (!accessToken) {
          return;
        }

        const data = await api.getComputations({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        });

        if (data) {
          const computations = Array.isArray(data) ? data : data.result || [];
          // Sort computations by creation date (newest first)
          const sortedComputations = [...computations].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setComputations(sortedComputations);

          // Auto-select the most recent computation
          if (sortedComputations.length > 0) {
            setSelectedComputation(sortedComputations[0].id);
          }
        } else {
          console.error("Unexpected API response format:", data);
        }
      } catch (error) {
        console.error("Error while fetching computations:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComputations();
  }, [accessToken]);

  /**
   * Effect to fetch chart data when the selected computation changes
   * Transforms the API response data into the format expected by chart components
   */
  useEffect(() => {
    const fetchComputationCharts = async () => {
      if (!selectedComputation || !accessToken) return;

      try {
        const data = await api.getComputationCharts(selectedComputation);

        if (data) {
          const result =
            Array.isArray(data) && data.length > 0 ? data[0].Items : [];
          const transformedData = {
            // Extract ECL (Expected Credit Loss) total data
            eclData: result.find((chart) => chart.Name === "SumEclTotal")
              ?.Data[0],

            // Transform rating breakdown data
            ratingData:
              result
                .find((chart) => chart.Name === "BreakdownByRating")
                ?.Data.map((item) => ({
                  rating: item.Rating,
                  value: item.EclOsiris,
                })) || [],

            // Transform issuer breakdown data
            issuerData:
              result
                .find((chart) => chart.Name === "BreakdownByIssuer")
                ?.Data.map((item) => ({
                  name: item.Issuer,
                  value: item.EclOsiris,
                })) || [],

            // Transform sector breakdown data
            sectorData:
              result
                .find((chart) => chart.Name === "BreakdownBySector")
                ?.Data.map((item) => ({
                  name: item.Sector,
                  value: item.EclOsiris,
                })) || [],

            // Transform maturity breakdown data
            maturityData:
              result
                .find((chart) => chart.Name === "BreakdownByMaturity")
                ?.Data.map((item) => ({
                  name: item.Interval,
                  value: item.SumEcl,
                })) || [],

            // Transform country breakdown data (includes bucket details)
            countryData:
              result
                .find((chart) => chart.Name === "BreakdownByCountry")
                ?.Data.map((item) => ({
                  name: item.Country,
                  bucket1: item.EclBucket1,
                  bucket2: item.EclBucket2,
                })) || [],

            // Transform country map data with additional total value
            countryMapData:
              result
                .find((chart) => chart.Name === "BreakdownByCountryMap")
                ?.Data.map((item) => ({
                  name: item.Country,
                  bucket1: item.EclBucket1,
                  bucket2: item.EclBucket2,
                  total: item.EclBucketByCountry,
                })) || [],

            // Transform bucket breakdown data
            bucketData:
              result
                .find((chart) => chart.Name === "BreakdownByBucket")
                ?.Data.map((item) => ({
                  name: item.Bucket,
                  value: item.EclOsiris,
                })) || [],

            // Extract total ECL amount
            totalEcl:
              result.find((chart) => chart.Name === "SumEclTotal")?.Data[0]
                ?.Amount || 0,
          };
          setChartData(transformedData);
        }
      } catch (error) {
        console.error("Error while fetching chart data:", error);
      }
    };

    fetchComputationCharts();
  }, [selectedComputation, accessToken]);

  /**
   * Component render - Multi-section dashboard layout with various chart components
   * Organized in a responsive grid system using Bootstrap Row/Col components
   */
  return (
    <div>
      {/* Header Row - ECL Summary and Computation Selector */}
      <Row className="g-3 mb-3">
        <Col md={5}>
          <EmptyCard chartData={chartData.eclData} />
        </Col>

        <Col md={7}>
          <PyramidCard className="h-100">
            <div className="d-flex flex-column h-100 py-2 justify-content-around">
              <div className="fw-medium fs-7 text-nowrap mt-n2">
                Computation
              </div>
              <SelectBox
                dataSource={groupedComputations}
                displayExpr="description"
                valueExpr="id"
                grouped={true}
                value={selectedComputation}
                onValueChanged={(e) => setSelectedComputation(e.value)}
                disabled={isLoading}
                searchEnabled={true}
                className="w-100"
              />
            </div>
          </PyramidCard>
        </Col>
      </Row>

      {/* Credit Rating Breakdown Section - Full Width */}
      <Row className="g-3 mb-3">
        <Col xs={12}>
          <BreakdownByRating
            data={chartData.ratingData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Three-Column Layout - Issuer, Bucket, and Sector Breakdowns */}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <BreakdownByIssuer
            data={chartData.issuerData}
            palette={currentPalette}
          />
        </Col>
        <Col md={4}>
          <BreakdownByBucket
            data={chartData.bucketData}
            palette={currentPalette}
          />
        </Col>
        <Col md={4}>
          <BreakdownBySector
            data={chartData.sectorData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Maturity Breakdown Section - Full Width */}
      <Row className="g-3 mb-3">
        <Col xs={12}>
          <BreakdownByMaturity
            data={chartData.maturityData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      {/* Two-Column Layout - Country Table and Geographic Map */}
      <Row className="g-3">
        <Col md={6}>
          <BreakdownByCountry
            data={chartData.countryData}
            palette={currentPalette}
          />
        </Col>
        <Col md={6}>
          <BreakdownByCountryMap
            data={chartData.countryMapData}
            palette={currentPalette}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ComputationCharts;
